import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project } from 'types/types';
import { decideAfterSave, decideBackAction } from 'lib/utils/backNavigation';
import { ProjectField } from 'lib/utils/projectPatch';

interface SaveArgs {
  formValues: Partial<Project>;
  leaveForm: boolean;
  fields: readonly ProjectField[];
  onRevertDeclined: () => void;
}

interface UseStepBackNavigationProps {
  /** Route to return to, e.g. `'/add-project/1'`. */
  previousPath: string;
  /** The fields this step owns — pass `STEP_FIELDS[n]`. Narrows the PATCH payload. */
  fields: readonly ProjectField[];
  /**
   * The values this step would save, shaped exactly as its forward handler shapes
   * them - step 2 nulls soil bearing for deep foundations, step 6 turns empty
   * strings into null, the quantity steps send only smqs. Pass the same function the
   * step's Next and Save & Exit handlers use, so Back cannot send a different payload.
   */
  buildFormValues: () => Partial<Project>;
  /** `formState.isDirty` from the step's react-hook-form instance. */
  isDirty: boolean;
  /** react-hook-form's `trigger`. Validates without submitting. */
  trigger: () => Promise<boolean>;
  /**
   * `handleCreateUpdateProject` from `useCreateUpdateProject`. Resolves true when saved,
   * and must call `onRevertDeclined` before resolving false if the user cancelled the
   * revert-to-Draft prompt — a false Back cannot explain becomes a discard prompt.
   */
  save: (args: SaveArgs) => Promise<boolean>;
}

/**
 * Back-button behaviour shared by every wizard step that has one (steps 2-8).
 *
 * One rule: **you can always go back; you can only go forward on a clean save.**
 * Save valid edits, ask before discarding ones that cannot be saved, never block the
 * way back.
 *
 * | Form state | Back does |
 * | --- | --- |
 * | not dirty | navigate |
 * | dirty and valid | save, then navigate |
 * | dirty and valid but the server rejects it | prompt: discard or stay |
 * | dirty and invalid | prompt: discard or stay |
 * | dirty and valid, but the user cancels the revert-to-Draft prompt | stay, edits intact |
 *
 * Discarding relies on `projectDetails` holding only last-saved server state, so the
 * step's next mount restores stored values. That invariant is why unsaved edits must
 * never be written into context — writing them there is what made a cleared field show
 * blank while the database still held its value.
 *
 * Back is never blocked, so a user cannot get stranded on a step they are unable to
 * satisfy. Callers should still disable the button while a save is in flight
 * (`isSubmitting || isLoading`) to stop a Back-save racing a Next-save.
 *
 * The decision itself lives in the pure, unit-tested `decideBackAction`; this hook only
 * performs it. Do not reimplement the branching in a page.
 *
 * @param props - Everything the step supplies; only these three differ between steps:
 * `previousPath`, `fields` and `buildFormValues`.
 * @param props.previousPath - Route to return to, e.g. `'/add-project/1'`.
 * @param props.fields - The fields this step owns — pass `STEP_FIELDS[n]`. Narrows the
 * PATCH payload so Back cannot resend another step's values.
 * @param props.buildFormValues - Returns the values this step would save, shaped exactly
 * as its forward handler shapes them (step 2 nulls soil bearing for deep foundations,
 * step 6 turns empty strings into null, the quantity steps send only `smqs`). Pass the
 * same function the step's Next and Save & Exit handlers use, so Back cannot send a
 * different payload than Next would.
 * @param props.isDirty - `formState.isDirty` from the step's react-hook-form instance.
 * Beware anything that calls `reset()` without `keepDirty`, which silently clears it.
 * @param props.trigger - react-hook-form's `trigger`. Validates without submitting.
 * @param props.save - `handleCreateUpdateProject` from `useCreateUpdateProject`. Must
 * resolve `true` only when the save actually succeeded, and must call the
 * `onRevertDeclined` callback it is passed when the user cancels the revert-to-Draft
 * prompt, so that choice is not mistaken for a rejected save.
 *
 * @example
 * const buildFormValues = () => updateFormValues(getValues());
 *
 * const { handleClickBack, showDiscardPrompt, cancelDiscard, confirmDiscard } =
 *   useStepBackNavigation({
 *     previousPath: '/add-project/1',
 *     fields: STEP_FIELDS[2],
 *     buildFormValues,
 *     isDirty,
 *     trigger,
 *     save: handleCreateUpdateProject,
 *   });
 *
 * // then render <DiscardChangesDialog open={showDiscardPrompt}
 * //   onCancel={cancelDiscard} onDiscard={confirmDiscard} />
 *
 * @returns `handleClickBack` for the Back button, plus the dialog's `showDiscardPrompt`
 * state and its `cancelDiscard` / `confirmDiscard` handlers.
 */
export const useStepBackNavigation = ({
  previousPath,
  fields,
  buildFormValues,
  isDirty,
  trigger,
  save,
}: UseStepBackNavigationProps) => {
  const navigate = useNavigate();
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false);

  const handleClickBack = async () => {
    const isValid = await trigger();
    const action = decideBackAction({ isDirty, isValid });

    if (action === 'navigate') {
      navigate(previousPath);
      return;
    }

    if (action === 'save') {
      // Set synchronously by the save before it resolves, so it is already true here.
      let revertDeclined = false;

      const saved = await save({
        formValues: buildFormValues(),
        leaveForm: false,
        fields,
        onRevertDeclined: () => { revertDeclined = true; },
      });

      const next = decideAfterSave({ saved, revertDeclined });

      if (next === 'navigate') {
        navigate(previousPath);
        return;
      }

      // The user declined to return the project to Draft. They stay on the step with
      // their edits, the project stays Submitted, and nothing is discarded.
      if (next === 'stay') return;
    }

    setShowDiscardPrompt(true);
  };

  const cancelDiscard = () => setShowDiscardPrompt(false);

  const confirmDiscard = () => {
    setShowDiscardPrompt(false);
    navigate(previousPath);
  };

  return { handleClickBack, showDiscardPrompt, cancelDiscard, confirmDiscard };
};
